<template>
  <q-page class="admin-certs-page">
    <div class="admin-certs-container">
      <!-- Header -->
      <div class="acert-header">
        <router-link to="/admin" class="acert-back-btn">
          <q-icon name="arrow_back" size="20px" />
        </router-link>
        <div>
          <h1 class="acert-title">Certificados</h1>
          <p class="acert-subtitle">{{ total }} certificados emitidos</p>
        </div>
      </div>

      <!-- List card -->
      <div class="acert-list-card">
        <!-- Loading -->
        <div v-if="loading" class="acert-center-msg">Cargando...</div>

        <!-- Error -->
        <div v-else-if="error" class="acert-center-msg">
          <p style="color: var(--oa-danger); margin-bottom: 8px;">{{ error }}</p>
          <q-btn flat no-caps label="Reintentar" style="color: var(--oa-gold-full);" @click="fetchCertificates" />
        </div>

        <!-- Empty -->
        <div v-else-if="certificates.length === 0" class="acert-center-msg">
          <q-icon name="verified" size="32px" style="color: var(--oa-text-disabled); margin-bottom: 8px;" />
          <br />
          No hay certificados emitidos
        </div>

        <!-- Certificate rows -->
        <template v-else>
          <div
            v-for="cert in certificates"
            :key="cert.id"
            class="acert-row"
          >
            <div class="acert-row-left">
              <div class="acert-icon-wrap">
                <q-icon name="emoji_events" size="16px" style="color: var(--oa-gold-full);" />
              </div>
              <div>
                <p class="acert-user-name">{{ cert.userName }}</p>
                <p class="acert-cert-number">{{ cert.certNumber }}</p>
              </div>
            </div>

            <div class="acert-row-right">
              <span class="acert-level">{{ levelLabels[cert.level] || cert.level }}</span>
              <q-badge
                :label="statusConfig(cert.status).label"
                :style="{
                  background: `color-mix(in srgb, ${statusConfig(cert.status).color} 12%, transparent)`,
                  color: statusConfig(cert.status).color,
                  fontSize: '11px',
                  padding: '2px 8px',
                  borderRadius: '6px',
                }"
              />
              <span v-if="cert.issuedAt" class="acert-date">{{ formatDate(cert.issuedAt) }}</span>
              <a
                :href="`/verificar/${encodeURIComponent(cert.certNumber)}`"
                target="_blank"
                rel="noopener noreferrer"
                class="acert-verify-link"
                title="Verificar certificado"
              >
                <q-icon name="open_in_new" size="14px" />
              </a>
            </div>
          </div>
        </template>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="acert-pagination">
          <span class="acert-page-info">Pagina {{ page }} de {{ totalPages }}</span>
          <div class="acert-page-btns">
            <q-btn
              flat
              round
              dense
              icon="chevron_left"
              :disable="page <= 1"
              style="color: var(--oa-text-muted);"
              @click="goPage(page - 1)"
            />
            <q-btn
              flat
              round
              dense
              icon="chevron_right"
              :disable="page >= totalPages"
              style="color: var(--oa-text-muted);"
              @click="goPage(page + 1)"
            />
          </div>
        </div>
      </div>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { api } from 'boot/axios';

// ---------- Types ----------
interface Certificate {
  id: string;
  certNumber: string;
  level: string;
  status: string;
  userName: string;
  issuedAt: string | null;
  expiresAt: string | null;
}

// ---------- State ----------
const loading = ref(true);
const error = ref<string | null>(null);
const certificates = ref<Certificate[]>([]);
const total = ref(0);
const page = ref(1);
const totalPages = ref(1);

// ---------- Constants ----------
const levelLabels: Record<string, string> = {
  cc: 'Coach Certified', sc: 'Senior Coach', c1: 'Coach Instructor',
  c2: 'Lead Trainer', ot_1: 'OT-1', ot_2: 'OT-2', ot_3: 'OT-3',
  owner: 'Owner', oa: 'Olympic Auditor', gj: 'Games Judge', kairos: 'Kairos',
};

const STATUS_STYLES: Record<string, { label: string; color: string }> = {
  certificado: { label: 'Valido', color: 'var(--oa-success)' },
  en_progreso: { label: 'En Progreso', color: 'var(--oa-tier-sc)' },
  requisitos_cumplidos: { label: 'Requisitos OK', color: 'var(--oa-gold-full)' },
  expirado: { label: 'Expirado', color: 'var(--oa-danger)' },
  en_gracia: { label: 'En Gracia', color: 'var(--oa-tier-ot)' },
  revocado: { label: 'Revocado', color: 'var(--oa-text-disabled)' },
};

// ---------- Helpers ----------
function statusConfig(status: string): { label: string; color: string } {
  return STATUS_STYLES[status] || { label: status, color: 'var(--oa-text-muted)' };
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function goPage(p: number) {
  page.value = p;
  fetchCertificates();
}

// ---------- Data Fetching ----------
async function fetchCertificates() {
  loading.value = true;
  error.value = null;
  try {
    const { data: res } = await api.get('/admin/certificates', {
      params: { page: page.value, limit: 20 },
    });
    certificates.value = res.items || [];
    total.value = res.total || 0;
    totalPages.value = res.totalPages || 1;
    page.value = res.page || 1;
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Error al cargar certificados';
    error.value = msg;
  } finally {
    loading.value = false;
  }
}

// ---------- Lifecycle ----------
onMounted(() => {
  fetchCertificates();
});
</script>

<style scoped>
.admin-certs-page {
  padding: 24px;
}

.admin-certs-container {
  max-width: 900px;
  margin: 0 auto;
}

/* Header */
.acert-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.acert-back-btn {
  padding: 8px;
  border-radius: 12px;
  color: var(--oa-text-muted);
  transition: background 0.15s ease;
  text-decoration: none;
}

.acert-back-btn:hover {
  background: var(--oa-bg-3);
}

.acert-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
}

.acert-subtitle {
  font-size: 0.875rem;
  color: var(--oa-text-muted);
  margin: 2px 0 0;
}

/* List card */
.acert-list-card {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 16px;
  overflow: hidden;
}

.acert-center-msg {
  padding: 32px;
  text-align: center;
  color: var(--oa-text-muted);
  font-size: 0.875rem;
}

/* Certificate row */
.acert-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
  transition: background 0.1s ease;
}

.acert-row:hover {
  background: color-mix(in srgb, var(--oa-bg-3) 30%, transparent);
}

.acert-row:last-child {
  border-bottom: none;
}

.acert-row-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.acert-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: color-mix(in srgb, var(--oa-gold-full) 10%, transparent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.acert-user-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--oa-text-primary);
  margin: 0;
}

.acert-cert-number {
  font-size: 0.6875rem;
  color: var(--oa-text-muted);
  font-family: var(--oa-font-mono);
  margin: 2px 0 0;
}

.acert-row-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.acert-level {
  font-size: 0.75rem;
  color: var(--oa-gold-full);
  font-weight: 500;
}

.acert-date {
  font-size: 0.6875rem;
  color: var(--oa-text-muted);
}

.acert-verify-link {
  padding: 6px;
  border-radius: 8px;
  color: var(--oa-text-muted);
  text-decoration: none;
  transition: background 0.15s ease, color 0.15s ease;
}

.acert-verify-link:hover {
  background: var(--oa-bg-3);
  color: var(--oa-gold-full);
}

/* Pagination */
.acert-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
}

.acert-page-info {
  font-size: 0.75rem;
  color: var(--oa-text-muted);
}

.acert-page-btns {
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>
